/**
 * 경과 시간 계산 유틸리티
 * 상수 및 공통 함수는 Lookups.ts와 constants/index.ts 활용
 */
import { getAcceptLabel } from './Lookups';
import {
  ELAPSED_THRESHOLDS,
  ELAPSED_STATUS
} from '../constants';
import type { ElapsedInfo, ElapsedStatus } from '../types';

/**
 * YYYYMMDDHHmmss (14자리) 형식 문자열을 밀리초로 변환
 */
export function parseYYYYMMDDHHmmssToMillis(s: string | number | null | undefined): number | null {
  if (s === undefined || s === null) return null;
  const str = String(s).trim();
  if (!/^[0-9]{14}$/.test(str)) return null;

  const y = Number(str.slice(0, 4));
  const mo = Number(str.slice(4, 6));
  const d = Number(str.slice(6, 8));
  const h = Number(str.slice(8, 10));
  const mi = Number(str.slice(10, 12));
  const ss = Number(str.slice(12, 14));

  const t = new Date(y, mo - 1, d, h, mi, ss).getTime();
  return isNaN(t) ? null : t;
}

/**
 * 밀리초를 'YYYY.MM.DD' 형식으로 변환
 */
function formatDateShort(millis: number): string {
  const d = new Date(millis);
  return `${d.getFullYear()}.${(d.getMonth() + 1).toString().padStart(2, '0')}.${d
    .getDate()
    .toString()
    .padStart(2, '0')}`;
}

/**
 * 밀리초를 'YYYY.MM.DD HH:mm' 형식으로 변환
 */
function formatDateFull(millis: number): string {
  const d = new Date(millis);
  return `${d.getFullYear()}.${(d.getMonth() + 1).toString().padStart(2, '0')}.${d
    .getDate()
    .toString()
    .padStart(2, '0')} ${d.getHours().toString().padStart(2, '0')}:${d
    .getMinutes()
    .toString()
    .padStart(2, '0')}`;
}

/**
 * 경과 시간(초)을 상대 시간 텍스트로 변환
 */
function getElapsedText(elapsedSeconds: number, oriMillis: number): string {
  if (elapsedSeconds <= 0) return '방금';
  if (elapsedSeconds < 60) return `${elapsedSeconds}초 전`;
  if (elapsedSeconds < 3600) return `${Math.floor(elapsedSeconds / 60)}분 전`;
  if (elapsedSeconds < ELAPSED_THRESHOLDS.ONE_DAY)
    return `${Math.floor(elapsedSeconds / 3600)}시간 전`;
  return formatDateShort(oriMillis);
}

/**
 * 경과 시간(초)을 상태 레벨로 변환
 */
function getElapsedStatusLevel(elapsedSeconds: number): ElapsedStatus {
  if (elapsedSeconds <= ELAPSED_THRESHOLDS.LEVEL1_MAX) return ELAPSED_STATUS.LEVEL1;
  if (elapsedSeconds <= ELAPSED_THRESHOLDS.LEVEL2_MAX) return ELAPSED_STATUS.LEVEL2;
  return ELAPSED_STATUS.LEVEL3;
}

/**
 * SERVICE 전용: YYYYMMDDHHmmss 형식 기반 경과 정보 계산
 */
export function computeElapsedInfoFromYYYYMMDD(
  nowTime: number,
  oriYYYY: string | number | null | undefined
): ElapsedInfo {
  const oriMillis = parseYYYYMMDDHHmmssToMillis(oriYYYY);

  if (!oriMillis || !nowTime) {
    return { status: null, text: null, full: null };
  }

  const elapsedSeconds = Math.floor(nowTime / 1000) - Math.floor(oriMillis / 1000);

  return {
    status: getElapsedStatusLevel(elapsedSeconds),
    text: getElapsedText(elapsedSeconds, oriMillis),
    full: formatDateFull(oriMillis)
  };
}

/**
 * VOC 전용: 경과 상태만 반환 (배지용)
 */
export function computeElapsedStatus(
  nowTime: number,
  oriDateTime: string | number | null | undefined,
  accept: string | null | undefined
): ElapsedStatus {
  if (!oriDateTime || !nowTime) return null;

  const oriMillis = Number(oriDateTime);
  if (isNaN(oriMillis)) return null;

  const elapsedSeconds = Math.floor(nowTime / 1000) - Math.floor(oriMillis / 1000);
  const isActive = ['대기', '접수', '보류'].includes(getAcceptLabel(accept));

  if (!isActive) return null;

  return getElapsedStatusLevel(elapsedSeconds);
}

/**
 * VOC 전용: 밀리초 타임스탬프 기반 경과 정보 계산
 */
export function computeElapsedInfo(
  nowTime: number,
  oriDateTime: string | number | null | undefined,
  accept: string | null | undefined
): ElapsedInfo {
  if (!oriDateTime || !nowTime) {
    return { status: null, text: null, full: null };
  }

  const oriMillis = Number(oriDateTime);
  if (isNaN(oriMillis)) {
    return { status: null, text: null, full: null };
  }

  const elapsedSeconds = Math.floor(nowTime / 1000) - Math.floor(oriMillis / 1000);
  const isActive = ['대기', '접수', '보류'].includes(getAcceptLabel(accept));

  if (!isActive) {
    return { status: null, text: null, full: null };
  }

  return {
    status: getElapsedStatusLevel(elapsedSeconds),
    text: getElapsedText(elapsedSeconds, oriMillis),
    full: formatDateFull(oriMillis)
  };
}
